<template>
   <div class="modal-rate-itinerary" @click.stop>
         <div class="title">{{ $t('rateItinerary.title') }}</div>

        <div class="content">
            <div class="subtitle">
                <I18nT :keypath="`rateItinerary.${profileType}.ratingSubtitle`" tag="p">
                    <template #break>
                        <br />
                    </template>
                    <template #itinerary>
                      <i>{{ mapStore.currentCategoryDetails?.document?.title }}</i>  
                    </template>
                    <template #required>
                        <span class="required">*</span>
                    </template>
                </I18nT>
            </div>

            <div class="stars">
                <div v-for="i in 5" :key="i" class="star" @click="setRating(i)" @mouseenter="hoverRating(i)" @mouseleave="resetHover">
                    <img :src="star3" :class="{ 'is-empty': i > currentRating }" alt="star">
                </div>
            </div>

            <template v-if="userStore.isAdministrator">
                <div class="subtitle">
                    <I18nT :keypath="`rateItinerary.${profileType}.pedagogicalRatingSubtitle`" tag="p">
                        <template #break>
                            <br />
                        </template>
                        <template #itinerary>
                        <i>{{ mapStore.currentCategoryDetails?.document?.title }}</i>  
                        </template>
                        <template #required>
                            <span class="required">*</span>
                        </template>
                    </I18nT>
                </div>

                <div class="stars">
                    <div v-for="i in 5" :key="i" class="star" @click="setRatingSecond(i)" @mouseenter="hoverRatingSecond(i)" @mouseleave="resetHoverSecond">
                        <img :src="star3" :class="{ 'is-empty': i > currentRatingSecond }" alt="star">
                    </div>
                </div>
            </template>

            <template v-if="userStore.isAdministrator">
                <div class="subtitle">
                    <I18nT :keypath="`rateItinerary.commentSubtitle`" tag="p">
                        <template #break>
                            <br />
                        </template>
                    </I18nT>
                </div>
                <div class="textarea-container">
                    <textarea
                        v-model="comment"
                        class="ui-textarea"
                        maxlength="500"
                        placeholder=""
                    ></textarea>
                    <div class="counter">{{ $t('rateItinerary.characterCounter', { length: comment.length }) }}</div>
                </div>
            </template>

            <div class="footer-message subtitle">
                {{ $t('rateItinerary.footerMessage') }}
            </div>
        </div>

        <ui-button 
            data-id="submit-btn" 
            :loading="loading" 
            :disabled="rating === 0 || (userStore.isAdministrator && ratingSecond === 0)" 
            class="ui-btn" 
            color="blue" 
            @click="submit"
        >
            {{ $t('rateItinerary.submitButton') }}
        </ui-button>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { evaluateItinerary, evaluateItinerarySecondQuestion } from '@/requests/stats-request'
import { useUserStore } from '@/stores/user-store'
import { useMapStore } from '@/stores/map-store'
import star3 from '@/assets/img/user/star-3.svg'
import { I18nT } from 'vue-i18n'

const mapStore = useMapStore()
const userStore = useUserStore()

const props = defineProps({
    modal: {
        type: Object,
        default: () => ({})
    }
})

const emits = defineEmits(['close'])
const rating = ref(0)
const hoveredRating = ref(0)
const ratingSecond = ref(0)
const hoveredRatingSecond = ref(0)
const comment = ref('')
const loading = ref(false)

const profileType = computed(() => userStore.isAdministrator ? 'manager' : 'other')

const currentRating = computed(() => {
    return hoveredRating.value || rating.value
})

const currentRatingSecond = computed(() => {
    return hoveredRatingSecond.value || ratingSecond.value
})

function setRating(index) {
    rating.value = index
}

function hoverRating(index) {
    hoveredRating.value = index
}

function resetHover() {
    hoveredRating.value = 0
}

function setRatingSecond(index) {
    ratingSecond.value = index
}

function hoverRatingSecond(index) {
    hoveredRatingSecond.value = index
}

function resetHoverSecond() {
    hoveredRatingSecond.value = 0
}

async function submit() {
    const categoryId = useMapStore().currentCategoryId
    if (rating.value === 0 || loading.value || !categoryId) return

    loading.value = true

    try {
        const commentNormalized = comment.value?.trim?.()
        await evaluateItinerary({
            categoryId: categoryId,
            rating: rating.value,
            comment: commentNormalized?.length > 0 ? commentNormalized : undefined
        })

        if (userStore.isAdministrator && ratingSecond.value > 0 && mapStore.currentCategoryDetails?.document?.proxyId) {
            await evaluateItinerarySecondQuestion({
                proxyId: mapStore.currentCategoryDetails?.document?.proxyId,
                rating: ratingSecond.value
            })
        }

        await userStore.augmentPreferences({
            path: `${categoryId}.hasRated`,
            value: true
        })

        emits('close')
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}
</script>

<style lang="stylus" scoped>
.modal-rate-itinerary
    max-width 728px
    width 90vw
    max-height 90vh
    background-color #fff
    padding 16px 32px 24px 32px 
    border-radius 16px
    display flex
    flex-direction column
    gap 24px
    align-items center



    .title
        border-bottom 2px solid #3B4851
        font-size 2.4rem
        font-weight 600
        text-transform uppercase
        text-align center
        padding-bottom 8px
        width 100%
        color #3B4851
        @media (max-width: 768px)
            font-size 2rem

    .content
        display flex
        flex-direction column
        gap 16px
        align-items center
        width 100%

        .subtitle
            display inline-block
            text-align center
            color #54606C
            font-size 1.8rem
            text-wrap pretty

        .stars
            display flex
            gap 8px
            cursor pointer
            max-width 100%
            justify-content center

            .star
                width 64px
                height auto
                aspect-ratio 1
                max-width calc((100% - 32px) / 5)
                transition transform 0.1s
                flex-shrink 0
                img
                    width 100%
                    height 100%
                    object-fit contain
                    &.is-empty
                        filter grayscale(100%)
                &:hover
                    transform scale(1.1)

        .textarea-container
            width 100%
            display flex
            flex-direction column
            gap 4px
            
            .ui-textarea
                width 100%
                height 120px
                max-height 50dvh
                border solid 1px #ccc
                border-radius 4px
                padding 8px
                resize none
                font-family inherit
                &:focus
                    outline none
                    border-color #3B4851

            .counter
                text-align right
                font-size 0.8rem
                color #999

    .ui-btn
        width 100%

.required
    color #FF0000
    font-weight 600
</style>